import { Circle } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { S4DProductComparison, S4DProductStatus } from '../utils/s4dProductComparison';
import { getS4DIssues } from '../utils/s4dIssueMessages';
import { S4DIssueList } from './S4DIssueList';

type Props = {
  comparison: S4DProductComparison;
  getIngredientName: (ingredientId: number) => string | null | undefined;
  /** Set when some sizes are priced by their recipe, which the list has no price for. */
  hasUncheckedPrices?: boolean;
};

export const s4dStatusColor = {
  [S4DProductStatus.Ok]: 'success.main',
  [S4DProductStatus.DataDiffers]: 'warning.main',
  [S4DProductStatus.LinkBroken]: 'error.main',
  [S4DProductStatus.NotCompared]: 'action.disabled',
} as const;

/**
 * Traffic light for how well a product's data matches S4D, shown before the product name: green
 * when everything matches, red when the product cannot be sold through S4D as it is (no product,
 * an inactive one, or a missing price or option), and yellow when anything else differs. The
 * tooltip lists what differs.
 */
export const S4DStatusIndicator = (props: Props): JSX.Element => {
  const { comparison, getIngredientName, hasUncheckedPrices } = props;
  const { t } = useTranslation();

  const issues = getS4DIssues({ comparison, t, getIngredientName });

  const title =
    comparison.status === S4DProductStatus.NotCompared ? (
      <Typography variant="body2" color="inherit">
        {t('Product is archived, S4D data is not compared')}
      </Typography>
    ) : (
      <>
        {issues.length === 0 ? (
          <Typography variant="body2" color="inherit">
            {t('S4D data matches')}
          </Typography>
        ) : (
          <S4DIssueList issues={issues} />
        )}
        {hasUncheckedPrices && (
          <Typography variant="caption" color="inherit" component="div" sx={{ mt: 0.5, opacity: 0.8 }}>
            {t('Prices of recipe-priced sizes are compared on the product page only')}
          </Typography>
        )}
      </>
    );

  return (
    <Tooltip title={title}>
      <Box display="flex" alignItems="center" sx={{ px: 0.25, cursor: 'default' }}>
        <Circle sx={{ fontSize: 10, color: s4dStatusColor[comparison.status] }} />
      </Box>
    </Tooltip>
  );
};
